import type firebase from 'firebase/app'

export type AuthSessionSignOutReason = 'signed-out-elsewhere'

export type AuthSessionLifecycleStatus =
  | 'initializing'
  | 'claiming'
  | 'active'
  | 'replaced-session'
  | 'unauthorized'
  | 'error'

export interface AuthSessionLifecycleState {
  readonly errorMessage?: string
  readonly signOutReason: AuthSessionSignOutReason | null
  readonly status: AuthSessionLifecycleStatus
}

export interface AuthSessionDerivedState {
  readonly isAuthenticated: boolean
  readonly isResolvingSession: boolean
  readonly isSigningOutForReplacedSession: boolean
}

export interface AuthSessionRouteDecision {
  readonly from?: string
  readonly kind: 'allow' | 'error' | 'loading' | 'redirect'
  readonly errorMessage?: string
  readonly loadingMessage?: string
  readonly reason?: AuthSessionSignOutReason | 'expired'
  readonly to?: string
}

interface PublicAuthRouteDecisionInput {
  readonly authenticatedRedirectPath: string
  readonly isAuthenticated: boolean
  readonly sessionState: AuthSessionLifecycleState
}

interface ProtectedAuthRouteDecisionInput {
  readonly fromPathname: string
  readonly isAuthenticated: boolean
  readonly sessionState: AuthSessionLifecycleState
  readonly userEmail: string | null | undefined
}

const checkingSessionDecision: AuthSessionRouteDecision = {
  kind: 'loading',
  loadingMessage: 'Checking session...',
}

const replacedSessionDecision: AuthSessionRouteDecision = {
  kind: 'loading',
  loadingMessage: 'Your account was opened on another device. Redirecting to sign-in...',
}

export function createInitialAuthSessionState(
  hasAuthUser: boolean,
): AuthSessionLifecycleState {
  return {
    signOutReason: null,
    status: hasAuthUser ? 'initializing' : 'unauthorized',
  }
}

export function getUnauthorizedAuthSessionState(
  signOutReason: AuthSessionSignOutReason | null = null,
): AuthSessionLifecycleState {
  return {
    signOutReason,
    status: 'unauthorized',
  }
}

export function getSignedOutAuthSessionState(
  signOutReason: AuthSessionSignOutReason | null = null,
): AuthSessionLifecycleState {
  return getUnauthorizedAuthSessionState(signOutReason)
}

export function getClaimingAuthSessionState(): AuthSessionLifecycleState {
  return {
    signOutReason: null,
    status: 'claiming',
  }
}

export function getResolvingAuthSessionState(): AuthSessionLifecycleState {
  return getClaimingAuthSessionState()
}

export function getActiveAuthSessionState(): AuthSessionLifecycleState {
  return {
    signOutReason: null,
    status: 'active',
  }
}

export function getReplacedAuthSessionState(): AuthSessionLifecycleState {
  return {
    signOutReason: 'signed-out-elsewhere',
    status: 'replaced-session',
  }
}

export function getErrorAuthSessionState(error: unknown): AuthSessionLifecycleState {
  return {
    errorMessage: error instanceof Error ? error.message : 'Unable to verify session.',
    signOutReason: null,
    status: 'error',
  }
}

export function getFailedAuthSessionState(error: unknown): AuthSessionLifecycleState {
  return getErrorAuthSessionState(error)
}

export function deriveAuthSessionState({
  sessionState,
  user,
}: {
  readonly sessionState: AuthSessionLifecycleState
  readonly user: firebase.User | null
}): AuthSessionDerivedState {
  return {
    isAuthenticated: sessionState.status === 'active' && Boolean(user),
    isResolvingSession:
      sessionState.status === 'initializing' ||
      sessionState.status === 'claiming',
    isSigningOutForReplacedSession: sessionState.status === 'replaced-session',
  }
}

export function getPublicAuthRouteDecision({
  authenticatedRedirectPath,
  isAuthenticated,
  sessionState,
}: PublicAuthRouteDecisionInput): AuthSessionRouteDecision {
  if (sessionState.status === 'error') {
    return {
      errorMessage: sessionState.errorMessage ?? 'Unable to verify session.',
      kind: 'error',
    }
  }

  if (sessionState.status === 'replaced-session') {
    return replacedSessionDecision
  }

  if (sessionState.status === 'initializing' || sessionState.status === 'claiming') {
    return checkingSessionDecision
  }

  if (isAuthenticated) {
    return {
      kind: 'redirect',
      to: authenticatedRedirectPath,
    }
  }

  return { kind: 'allow' }
}

export function getProtectedAuthRouteDecision({
  fromPathname,
  isAuthenticated,
  sessionState,
  userEmail,
}: ProtectedAuthRouteDecisionInput): AuthSessionRouteDecision {
  if (sessionState.status === 'error') {
    return {
      errorMessage: sessionState.errorMessage ?? 'Unable to verify session.',
      kind: 'error',
    }
  }

  if (sessionState.status === 'replaced-session') {
    return replacedSessionDecision
  }

  if (sessionState.status === 'initializing' || sessionState.status === 'claiming') {
    return checkingSessionDecision
  }

  if (!isAuthenticated) {
    return {
      from: fromPathname,
      kind: 'redirect',
      reason: sessionState.signOutReason ?? undefined,
      to: '/login',
    }
  }

  if (!userEmail) {
    return {
      kind: 'redirect',
      reason: 'expired',
      to: '/login',
    }
  }

  return { kind: 'allow' }
}